'use client'

import { ProductSlider } from '@/feature/product/components/product-slider/ProductSlider.client'
import { filterAvailableProducts } from '@/feature/product/services/product.filtering'
import { APOLLO_CLIENT_SIDE_OPTIONS } from '@/lib/apollo/clients/apolloClient.client'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { isSSR } from '@/lib/next/services/nextjs.rendering'
import { Section } from '@/ui/layouts/section/Section'
import { Loader } from '@/ui/loaders/loader/Loader'
import { useQuery } from '@apollo/client'
import { gql } from '@gql-generated/gql'
import {
  GetLinkedProductsConfigDocument,
  GetLinkedProductsSliderDocument,
  ProductType,
} from '@gql-generated/graphql'

export const GET_LINKED_PRODUCTS_SLIDER_QUERY = gql(/* GraphQL */ `
  query GetLinkedProductsSlider($urlKey: String!) {
    product(urlKey: $urlKey) {
      reference
      type
      linkedProducts {
        ...ProductCard
      }
    }
  }
`)

type LinkedProductsSliderProps = {
  slug: string

  /** Indicates if the AddProductToFavoriteButton is displayed */
  isAddableToFavorites?: boolean

  /** Additional classes to add to the ProductSlider */
  sliderClassName?: string

  /** The source of the product slider */
  source?: string
}

export function LinkedProductsSlider({
  slug,
  isAddableToFavorites = true,
  sliderClassName,
  source,
}: LinkedProductsSliderProps): React.ReactElement | null {
  const { t } = useTranslation(['linkedProductsSlider'])
  const { data: configData } = useQuery(GetLinkedProductsConfigDocument, APOLLO_CLIENT_SIDE_OPTIONS)

  const isLinkedProductEnabled = configData?.config?.catalog?.isLinkedProductEnabled

  const { data, loading } = useQuery(GetLinkedProductsSliderDocument, {
    ...APOLLO_CLIENT_SIDE_OPTIONS,
    variables: {
      urlKey: slug,
    },
    skip: !isLinkedProductEnabled || isSSR,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-only',
  })

  const analyticsSource =
    data?.product?.type === ProductType.Article
      ? 'produits complémentaires - fiche article'
      : 'produits complémentaires - fiche produit'

  const linkedProducts = filterAvailableProducts(data?.product?.linkedProducts ?? [])

  if (!isLinkedProductEnabled || (!loading && !linkedProducts.length)) {
    return null
  }

  return (
    <>
      {loading ? (
        <div className="flex justify-center py-2">
          <Loader />
        </div>
      ) : (
        <Section title={t('linkedProductsSlider:title')}>
          <ProductSlider
            products={linkedProducts}
            isAddableToFavorites={isAddableToFavorites}
            className={sliderClassName}
            source={source ? source : analyticsSource}
          />
        </Section>
      )}
    </>
  )
}
